import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Lesson video player with a moving watermark of the viewer's name.
//
// YouTube videos: YouTube's own controls are switched off and a transparent shield covers the
// whole picture, so its Copy link, Share, Watch later, "Watch on YouTube" buttons and its
// right-click menu cannot be used. The player is driven through YouTube's IFrame API and has
// its own play / seek / volume / speed controls. The student's name floats across the picture.
//
// What it cannot do: stop screen recording, or hide the video address from someone who
// inspects the page's code. The moving name is there so a recording can be traced.

const YT_ORIGIN = 'https://www.youtube.com'; // the live site's security policy allows frames from this host
const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

function youTubeId(url) {
  if (!url || !(url.includes('youtube.com') || url.includes('youtu.be'))) return null;
  return url.split('v=')[1]?.split('&')[0] || url.split('be/')[1]?.split('?')[0] || url.split('/embed/')[1]?.split('?')[0] || null;
}

function otherEmbedUrl(url) {
  if (!url) return '';
  if (url.includes('vimeo.com')) {
    const id = url.split('vimeo.com/')[1]?.split('?')[0];
    return `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&dnt=1&fullscreen=0`;
  }
  return url;
}

function youTubeEmbedSrc(id) {
  const params = new URLSearchParams({
    enablejsapi: '1', // lets this page send play / pause / seek messages to the player
    controls: '0', rel: '0', modestbranding: '1', disablekb: '1', fs: '0', iv_load_policy: '3',
    playsinline: '1', cc_load_policy: '0', origin: window.location.origin,
  });
  return `${YT_ORIGIN}/embed/${id}?${params.toString()}`;
}

const fmt = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(t / 60);
  const sec = String(t % 60).padStart(2, '0');
  return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

function ProtectedVideoPlayer({ youtubeLink, session = {} }) {
  const videoId = useMemo(() => youTubeId(youtubeLink), [youtubeLink]);
  const [expanded, setExpanded] = useState(false);
  const [spot, setSpot] = useState({ x: 8, y: 12 });
  const [spot2, setSpot2] = useState({ x: 60, y: 70 });
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState({ now: 0, total: 0 });
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [speed, setSpeed] = useState(1);
  const frameRef = useRef(null);

  const label = useMemo(() => {
    const name = [session.firstName, session.lastName].filter(Boolean).join(' ') || session.username || 'Registered student';
    return `${name} · ${session.username || session.id || ''}`;
  }, [session]);

  // Drift the name to a new spot every few seconds.
  useEffect(() => {
    const move = (set) => set({ x: 4 + Math.random() * 62, y: 10 + Math.random() * 68 });
    const a = setInterval(() => move(setSpot), 7000);
    const b = setInterval(() => move(setSpot2), 11000);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  // ---- talk to the YouTube player with postMessage ----------------------------------------
  // No extra script is loaded, so the site's security policy needs no change.
  const send = useCallback((payload) => {
    const w = frameRef.current && frameRef.current.contentWindow;
    if (w) w.postMessage(JSON.stringify(payload), YT_ORIGIN);
  }, []);
  const cmd = useCallback((func, args = []) => send({ event: 'command', func, args }), [send]);

  // New video: reset the display.
  useEffect(() => {
    setReady(false);
    setFailed(false);
    setPlaying(false);
    setTime({ now: 0, total: 0 });
  }, [videoId]);

  // Listen to what the player reports (state, clock, volume).
  useEffect(() => {
    if (!videoId) return undefined;
    const onMessage = (e) => {
      if (e.origin !== YT_ORIGIN || !frameRef.current || e.source !== frameRef.current.contentWindow) return;
      let data = e.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (err) { return; }
      }
      if (!data || typeof data !== 'object') return;
      if (data.event === 'onReady') setReady(true);
      if (data.event === 'onError') setFailed(true);
      if (data.event === 'onStateChange') setPlaying(data.info === 1 || data.info === 3);
      if (data.event === 'infoDelivery' && data.info) {
        const i = data.info;
        setReady(true);
        if (typeof i.playerState === 'number') setPlaying(i.playerState === 1 || i.playerState === 3);
        if (typeof i.currentTime === 'number' || typeof i.duration === 'number') {
          setTime((t) => ({ now: typeof i.currentTime === 'number' ? i.currentTime : t.now, total: typeof i.duration === 'number' && i.duration > 0 ? i.duration : t.total }));
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [videoId]);

  // After the frame loads, ask the player to start reporting.
  const onFrameLoad = () => {
    send({ event: 'listening', id: 1, channel: 'widget' });
    setTimeout(() => send({ event: 'listening', id: 1, channel: 'widget' }), 800);
    setTimeout(() => setReady(true), 1500);
  };

  // If it never answers, say so instead of staying on "Loading".
  useEffect(() => {
    if (!videoId || ready) return undefined;
    const t = setTimeout(() => setFailed(true), 20000);
    return () => clearTimeout(t);
  }, [videoId, ready]);

  const toggle = useCallback(() => {
    if (!ready) return;
    cmd(playing ? 'pauseVideo' : 'playVideo');
    setPlaying((v) => !v);
  }, [playing, ready, cmd]);

  const seek = (value) => {
    cmd('seekTo', [Number(value), true]);
    setTime((t) => ({ ...t, now: Number(value) }));
  };
  const changeVolume = (value) => {
    const v = Number(value);
    setVolume(v);
    cmd('setVolume', [v]);
    if (v > 0 && muted) { cmd('unMute'); setMuted(false); }
  };
  const toggleMute = () => {
    if (muted) { cmd('unMute'); setMuted(false); } else { cmd('mute'); setMuted(true); }
  };
  const changeSpeed = (value) => {
    const v = Number(value);
    setSpeed(v);
    cmd('setPlaybackRate', [v]);
  };

  const tag = (pos, opacity) => ({
    position: 'absolute',
    left: `${pos.x}%`,
    top: `${pos.y}%`,
    transition: 'left 3s ease-in-out, top 3s ease-in-out',
    color: '#fff',
    opacity,
    fontSize: 15,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4,
    background: 'rgba(0,0,0,0.18)',
    textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    zIndex: 3,
  });

  const frameStyle = expanded
    ? { position: 'fixed', inset: 0, zIndex: 3000, background: '#000' }
    : { position: 'relative', width: '100%', height: 400, background: '#000' };

  const barButton = { border: 0, background: 'transparent', color: '#fff', fontSize: 15, cursor: 'pointer', padding: '2px 6px' };

  const watermarkAndExpand = (
    <>
      <span style={tag(spot, 0.55)}>{label}</span>
      <span style={tag(spot2, 0.3)}>{label}</span>
    </>
  );

  // ---- non-YouTube links: plain embed, with the watermark on top ----------------------
  if (!videoId) {
    return (
      <div className="rounded-16 overflow-hidden" style={{ position: 'relative' }}>
        <div style={{ position: 'relative', width: '100%', height: 400 }}>
          <iframe
            width="100%"
            height="400"
            src={otherEmbedUrl(youtubeLink)}
            title="Lesson Video"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            style={{ display: 'block' }}
          />
          {watermarkAndExpand}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-16 overflow-hidden" style={expanded ? undefined : { position: 'relative' }}>
      <div style={frameStyle}>
        <iframe
          key={videoId}
          ref={frameRef}
          src={youTubeEmbedSrc(videoId)}
          title="Lesson Video"
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={onFrameLoad}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />

        {/* Shield over the whole picture: nothing in YouTube's own player can be clicked. */}
        <div
          onClick={toggle}
          onContextMenu={(e) => e.preventDefault()}
          style={{ position: 'absolute', inset: 0, zIndex: 2, cursor: ready ? 'pointer' : 'default' }}
        >
          {ready && !playing && (
            <span
              style={{
                position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 72, height: 72,
                borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 34, paddingLeft: 6,
              }}
            >
              ▶
            </span>
          )}
          {!ready && !failed && <span style={{ position: 'absolute', left: 14, bottom: 14, color: '#fff', fontSize: 13 }}>Loading video…</span>}
          {failed && <span style={{ position: 'absolute', left: 14, bottom: 14, color: '#fff', fontSize: 13 }}>This video could not be loaded. Please refresh the page.</span>}
        </div>

        {watermarkAndExpand}

        {/* Our own controls */}
        <div
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5, padding: '6px 10px', display: 'flex', alignItems: 'center',
            gap: 8, background: 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))', color: '#fff',
          }}
        >
          <button type="button" style={{ ...barButton, fontSize: 18 }} onClick={toggle} disabled={!ready} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? '❚❚' : '▶'}
          </button>
          <span style={{ fontSize: 12, minWidth: 84, textAlign: 'center' }}>{fmt(time.now)} / {fmt(time.total)}</span>
          <input
            type="range"
            min="0"
            max={Math.max(1, Math.floor(time.total))}
            step="1"
            value={Math.min(Math.floor(time.now), Math.max(1, Math.floor(time.total)))}
            onChange={(e) => seek(e.target.value)}
            disabled={!ready}
            aria-label="Seek"
            style={{ flex: 1, accentColor: '#22c55e' }}
          />
          <button type="button" style={barButton} onClick={toggleMute} disabled={!ready} aria-label={muted ? 'Unmute' : 'Mute'}>{muted || volume === 0 ? '🔇' : '🔊'}</button>
          <input type="range" min="0" max="100" value={muted ? 0 : volume} onChange={(e) => changeVolume(e.target.value)} disabled={!ready} aria-label="Volume" style={{ width: 70, accentColor: '#22c55e' }} />
          <select
            value={speed}
            onChange={(e) => changeSpeed(e.target.value)}
            disabled={!ready}
            aria-label="Playback speed"
            style={{ background: 'rgba(0,0,0,0.5)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, fontSize: 12 }}
          >
            {SPEEDS.map((s) => <option key={s} value={s} style={{ color: '#000' }}>{s}x</option>)}
          </select>
          <button type="button" style={{ ...barButton, fontSize: 13 }} onClick={() => setExpanded((v) => !v)} title={expanded ? 'Close (Esc)' : 'Expand'}>
            {expanded ? 'Close ✕' : 'Expand ⤢'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProtectedVideoPlayer;
